import React, { useEffect, useState } from 'react';
import { DatePicker, Form, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import './OrderGuide.css';

export interface PrepaymentPaidDialogProps {
  open: boolean;
  /** Saved 'YYYY-MM-DD', null = not paid yet. */
  paidAt: string | null;
  saving: boolean;
  /** 'YYYY-MM-DD', or null to clear a day entered by mistake. */
  onSave: (paidAt: string | null) => void;
  onCancel: () => void;
  /**
   * „Klient potwierdził” of a prepaid ZK without a payment day (Emil,
   * 2026-11-02): „Księgowość potwierdziła wpłatę?” — Tak saves the day and
   * the page confirms the order next; Nie leaves it waiting for the payment.
   */
  gate?: boolean;
}

/**
 * „Wpłata z proformy” (ZK paid in advance, decision 2026-10-27): the day the
 * customer paid. Only the day — the amount is on the proforma itself; clearing
 * the field removes a day entered by mistake. Saving moves nothing: the
 * operator confirms the order next („Klient potwierdził”).
 */
export default function PrepaymentPaidDialog({ open, paidAt, saving, onSave, onCancel, gate = false }: PrepaymentPaidDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const [value, setValue] = useState<Dayjs | null>(null);
  useEffect(() => {
    if (open) setValue(paidAt ? dayjs(paidAt) : dayjs());
  }, [open, paidAt]);

  return (
    <Modal
      open={open}
      title={t(gate ? 'orders:paymentMode.paymentGate.title' : 'orders:paymentMode.paidDialog.title')}
      okText={t(gate ? 'orders:paymentMode.paymentGate.yes' : 'common:save')}
      cancelText={t(gate ? 'orders:paymentMode.paymentGate.no' : 'common:cancel')}
      confirmLoading={saving}
      // The gate records a real payment day: an empty field would confirm an unpaid order.
      okButtonProps={{ disabled: gate && !(value && value.isValid()) }}
      onOk={() => onSave(value && value.isValid() ? value.format('YYYY-MM-DD') : null)}
      onCancel={onCancel}
      destroyOnHidden
      width={gate ? 520 : 420}
      data-testid="zk-prepayment-paid-dialog"
    >
      {gate && <Typography.Paragraph>{t('orders:paymentMode.paymentGate.text')}</Typography.Paragraph>}
      <Form layout="vertical" component="div" className="zk-prepayment-paid">
        <Form.Item label={t('orders:paymentMode.paidDialog.date')} extra={t('orders:paymentMode.paidDialog.help')}>
          <DatePicker
            value={value}
            onChange={setValue}
            format="DD.MM.YYYY"
            allowClear
            className="zk-prepayment-paid__date"
            aria-label={t('orders:paymentMode.paidDialog.date')}
            data-testid="zk-prepayment-paid-date"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
